// Global rail. Two states:
//   - expanded (w-60): logo + labels + nav + footer chip
//   - collapsed (w-14): icon rail only, label as tooltip
// State persisted via useUi (zustand + localStorage).

import { ThemeToggle } from '@/components/ui/theme-toggle';
import { useInboxStats } from '@/hooks/use-inbox-stats';
import { cn } from '@/lib/cn';
import { useUi } from '@/stores/ui';
import { ClipboardCheck, GitBranch, Layers, PanelLeft } from 'lucide-react';
import type { ComponentType } from 'react';
import { Link, useLocation } from 'react-router';

interface NavItem {
  href: string;
  label: string;
  Icon: ComponentType<{ className?: string; strokeWidth?: number }>;
}

const NAV: NavItem[] = [
  { href: '/review', label: 'Review', Icon: ClipboardCheck },
  { href: '/concepts', label: 'Concepts', Icon: Layers },
  { href: '/dag', label: 'Graph', Icon: GitBranch },
];

export function StudioSidebar() {
  const { pathname } = useLocation();
  const collapsed = useUi((s) => s.railCollapsed);
  const toggle = useUi((s) => s.toggleRail);
  // Inbox stats badge on the Review item — same query key as the queue
  // pane, TanStack dedupes.
  const inboxStats = useInboxStats();
  const badge = inboxStats
    ? inboxStats.stats.stale > 0
      ? { count: inboxStats.stats.stale + inboxStats.stats.awaiting, tone: 'destructive' as const }
      : inboxStats.stats.awaiting > 0
        ? { count: inboxStats.stats.awaiting, tone: 'neutral' as const }
        : null
    : null;

  return (
    <aside
      className={cn(
        'hidden shrink-0 flex-col border-r border-[var(--color-border)] bg-[var(--color-surface)] md:flex',
        collapsed ? 'w-14' : 'w-60',
      )}
    >
      <div
        className={cn(
          'flex border-b border-[var(--color-border)] py-4',
          collapsed ? 'flex-col items-center gap-3 px-2' : 'items-center gap-2 px-3',
        )}
      >
        <button
          type="button"
          onClick={toggle}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="grid size-8 shrink-0 place-items-center rounded-md text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)] hover:text-[var(--color-foreground)]"
        >
          <PanelLeft className="size-4" strokeWidth={1.75} />
        </button>
        {!collapsed && (
          <div className="flex min-w-0 flex-1 flex-col leading-tight">
            <span className="text-sm font-semibold tracking-tight">Disha Studio</span>
            <span className="text-[10px] uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
              Mentor
            </span>
          </div>
        )}
        <ThemeToggle />
      </div>
      <nav className={cn('flex-1 overflow-y-auto py-4 text-sm', collapsed ? 'px-2' : 'px-3')}>
        <ul className="space-y-0.5">
          {NAV.map((item) => {
            const active = pathname === item.href || pathname.startsWith(`${item.href}/`);
            const showBadge = item.href === '/review' && badge;
            return (
              <li key={item.href}>
                <Link
                  to={item.href}
                  title={collapsed ? item.label : undefined}
                  className={cn(
                    'flex items-center rounded-lg',
                    collapsed ? 'justify-center px-0 py-2' : 'gap-3 px-2.5 py-2',
                    active
                      ? 'bg-[var(--color-accent)] text-[var(--color-accent-foreground)]'
                      : 'text-[var(--color-foreground)] hover:bg-[var(--color-muted)]',
                  )}
                >
                  <item.Icon
                    className={cn(
                      'size-4 shrink-0',
                      active
                        ? 'text-[var(--color-primary)]'
                        : 'text-[var(--color-muted-foreground)]',
                    )}
                    strokeWidth={1.75}
                  />
                  {!collapsed && (
                    <span className="flex-1 truncate text-[13px] font-medium">{item.label}</span>
                  )}
                  {!collapsed && showBadge && badge && (
                    <span
                      className={cn(
                        'ml-auto rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums',
                        badge.tone === 'destructive'
                          ? 'bg-[var(--color-destructive)] text-[var(--color-destructive-foreground)]'
                          : 'bg-[var(--color-muted)] text-[var(--color-muted-foreground)]',
                      )}
                    >
                      {badge.count}
                    </span>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
    </aside>
  );
}
